import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getNotificationsBySite } from '../../app/utils/Notifications';
import { getAgentTokens, getSites } from '../../app/utils/Sites';
import { USER_FORM } from '../../app/utils/Users';
import { setTrialMode, setAdminScreen } from '../session/sessionSlice';

const initialState = {
  model: {},
  error: null,
  status: ""
};

const fillPeriodObj = (i, y1, m1, d1, y2, m2, d2) => ({
    id: i,
    start: new Date(y1, m1, d1).toLocaleString('en-US', {month: 'short', day: 'numeric', year: '2-digit'}),
    startISO: new Date(y1, m1, d1, 14, 0, 0).toISOString().substr(0, 10),
    end: new Date(y2, m2, d2).toLocaleString('en-US', {month: 'short', day: 'numeric', year: '2-digit'}),
    endISO: new Date(y2, m2, d2, 14, 0, 0).toISOString().substr(0, 10),
})

export const getSitesAsync = createAsyncThunk(
  'site/get',
  async (user, thunkAPI) => {
    try {
        const state = thunkAPI.getState()

        const {response, error} = await getSites(state.session.token)
        const all = response.result

        if(error){
            throw error
        }

        let periods = []
        let periodsByYear = []
        let dt = new Date(), year = dt.getFullYear(), month = dt.getMonth(), date = dt.getDate(), incr = 0
        
        periods.push(fillPeriodObj(0, year, month, date-31, year, month, date-1))
        for (let i=1; i<7; i++) {
            periods.push(fillPeriodObj(i, year, month-i, 1, year, month-i+1, 0))
        }

        for(let i = year; i >= year - 1; i --){
          periodsByYear.push({
              id: incr,
              start: i-1,
              end: i
          })
          incr++
        }

        let userSite = {}

        let reseller = null
        let resellerToken = null
        let isAffiliate = false

        let salesTokens = []

        if(!state.session.isAdmin){
          userSite = all.filter(x => x.siteId === user.siteId)[0]
          if (reseller = userSite && userSite.type === 'TOKEN') {
            resellerToken = `/${user.siteId}`

            if(userSite.attr.isAffiliate){
              isAffiliate = true
            }

            salesTokens = [userSite]
          }
        } else {
          const { response, error } = await getAgentTokens()
          if(response && response.salesTokens){
            salesTokens = response.salesTokens
              .sort((a, b) => a.name !== null && b.name !== null && a.name.toUpperCase()>b.name.toUpperCase() ? 1 : a.name===b.name ? 0 : -1)
          }
        }

        const sites = all.filter(x => x.type === "SITE")
        let trialMode = false
        if(sites.length === 1 && sites[0].attr.trialExpiryDate){
			    trialMode = true
        }        

        const topOrganizations = all.filter(x => x.type === 'TOP_ORG')

        const organizations = all.filter(x => x.type === 'ORG' || x.type === 'TOP_ORG')
        .sort((a, b) => a.name !== null && b.name !== null && a.name.toUpperCase()>b.name.toUpperCase() ? 1 : a.name===b.name ? 0 : -1)

        const orgFixed = !reseller && organizations.length === 1
        const singleSiteUser = !reseller && sites.find(x => user.siteId === x.siteId) && sites.length === 1

        const tokenFixed = reseller && salesTokens.length === 1

        const siteForm = {name: '', attr: {
          url: '', 
          tz: 'Pacific', 
          accounts: {}, 
          address: '', 
          city: '', 
          state: '', 
          zip: '', 
          cellPhone: '', 
          businessPhone: ''
        }, type: 'SITE'}

        if(reseller){
          siteForm.attr.isEcommerce = true
        }

        return {
          logo: false,
          trialMode,
          tokenFixed,
          salesTokens,
          notification: {
            status: "idle",
            items: [],
            lastEvalKey: {}
          },
          abortController: new AbortController(),
          userSite,
          orgFixed,
          singleSiteUser,
          reseller,
          isAffiliate,
          resellerToken,
          tokenActiveIdx: -1,
          topOrgActiveIdx: -1,
          orgActiveIdx: -1,
          regActiveIdx: -1,
          siteActiveIdx: -1,
          period: -1,
          topOrgForm: {
            name: '',
            attr: { city: '', state: 'AL', zip: ''},
            type: 'TOP_ORG',
          },
          orgForm: {
            name: '',
            attr: { city: '', state: 'AL', zip: '', token: (reseller ? userSite.siteId : '')},
            type: 'ORG',
          },
          tokenForm: {
            name: '',
            disableAlertNotificationView: true,
            attr: {
              isAffiliate: true
            },
            type: 'TOKEN',
          },
          regForm: {name: '', attr: {dummy: ''}, type: 'REG'},
          siteForm,
          all,
          regions: [],
          sites: orgFixed ? sites : [],
          periods,
          periodsByYear,
          organizations,
          topOrganizations,
          organizationsFilter: [...organizations],
          sitesFilter: [],
          userForm: USER_FORM,
          users: [],
          resetFilteredText: false
        }
    } catch(e) {
      return {error: e}
    }
  }
);

export const tokenChange = createAsyncThunk(
  'site/token',
  async (idx, thunkAPI) => {
    const state = thunkAPI.getState()
    const append = {}
    if (idx !== undefined) {
      append.tokenActiveIdx = idx
    }
    
    let periodIndex
    
    if(state.site.model.period === -1){
      periodIndex = 0
    }
    else{
      periodIndex = state.site.model.period > state.site.model.periods.length - 1 ? 0 : state.site.model.period
    }
    
    append.period = parseInt(periodIndex)
    
    await thunkAPI.dispatch(appendModel(append))
    
    await thunkAPI.dispatch(periodChange(periodIndex))
  })

export const topOrgChange = createAsyncThunk(
  'site/topOrg',
  async (idx, thunkAPI) => {
    const state = thunkAPI.getState()
    const append = {}
    if (idx !== undefined) {
      append.topOrgActiveIdx = idx

      append.orgActiveIdx = state.site.model.organizations.findIndex(x => x.siteId === state.site.model.topOrganizations[idx].siteId) ?? 0
    }

    append.regions = state.site.model.all.filter(x => x.type === 'REG' &&
    x.sitePath.startsWith(state.site.model.topOrganizations[idx].sitePath))
    .sort((a, b) => a.name>b.name ? 1 : a.name===b.name ? 0 : -1)
    
    let regionIndex
    
    if(state.site.model.regActiveIdx === -1){
      regionIndex = "0"
    }
    else{
      regionIndex = state.site.model.regActiveIdx > state.site.model.regions.length - 1 ? "0" : state.site.model.regActiveIdx
    }
    
    append.regActiveIdx = regionIndex
    append.regFixed = state.site.model.orgFixed && append.regions.length === 1
    
    await thunkAPI.dispatch(appendModel(append))
    
    await thunkAPI.dispatch(regChange(append.regFixed ? "0" : regionIndex))
  })

export const orgChange = createAsyncThunk(
  'site/org',
  async (idx, thunkAPI) => {
    const state = thunkAPI.getState()
    const append = {}
    if (idx!==undefined) {
      append.orgActiveIdx = idx
      append.topOrgActiveIdx = state.site.model.topOrganizations.findIndex(x => x.siteId === state.site.model.organizations[idx].siteId) ?? 0
    }
    
    const logo = state.site.model.organizations[idx].attr !== null ? 
    state.site.model.organizations[idx].attr.logo : false
    
    const militaryBranch = state.site.model.organizations[idx].attr !== null ? 
    state.site.model.organizations[idx].attr.militaryBranch : false
    
    
    append.logo = logo
    append.militaryBranch = militaryBranch
    
    append.regions = state.site.model.all.filter(x => x.type === 'REG' &&
    x.sitePath.startsWith(state.site.model.organizations[idx].sitePath))
    .sort((a, b) => a.name>b.name ? 1 : a.name===b.name ? 0 : -1)
    
    let regionIndex
    
    if(state.site.model.regActiveIdx === -1){
      regionIndex = "0"
    }
    else{
      regionIndex = state.site.model.regActiveIdx
    }
    
    append.regActiveIdx = regionIndex
    append.regFixed = state.site.model.orgFixed && append.regions.length === 1
    
    await thunkAPI.dispatch(appendModel(append))
    
    await thunkAPI.dispatch(regChange(append.regFixed ? "0" : regionIndex))
  })
  
  export const regChange = createAsyncThunk(
    'site/reg',
    async (idx, thunkAPI) => {
      const state = thunkAPI.getState()
      const append = {}
      
      if (idx!==undefined && idx !== null) {
        append.regActiveIdx = idx > state.site.model.regions.length - 1 ? "0" : idx
      }
      idx = append.regActiveIdx
      if (idx<0) {
        append.sites = []
        append.sitesFilter = []
        return
      }

      if(state.site.model.regions[idx]?.linkedSites && Array.isArray(state.site.model.regions[idx]?.linkedSites)){
        append.sites = state.site.model.all.filter(x => x.type === 'SITE' &&
        state.site.model.regions[idx]?.linkedSites?.includes(x.siteId)
        ).map(s => {
          if(state.site.model.regions[idx]?.linkedSiteNickNames && 
            state.site.model.regions[idx]?.linkedSiteNickNames[s.siteId]){

            return {
              ...s,
              name: state.site.model.regions[idx]?.linkedSiteNickNames[s.siteId]
            } 
          }

          return s
        })
      } else {
        append.sites = state.site.model.regions[idx] ? state.site.model.all.filter(x => x.type === 'SITE' &&
        x.sitePath.startsWith(state.site.model.regions[idx].sitePath))
        .sort((a, b) => a.name>b.name ? 1 : a.name===b.name ? 0 : -1) : []
      }
      append.siteFixed = state.site.model.regFixed && append.sites.length === 1
      
      let siteIndex
      
      if(state.site.model.siteActiveIdx === -1){
        siteIndex = "0"
      }
      else{
        siteIndex = state.site.model.siteActiveIdx
      }
      
      append.siteActiveIdx = siteIndex

      append.sitesFilter = [...append.sites]
      
      await thunkAPI.dispatch(appendModel(append))
      
      await thunkAPI.dispatch(siteChange(append.siteFixed ? "0" : siteIndex))
    })

export const siteChange = createAsyncThunk(
  'site/site',
  async (idx, thunkAPI) => {
    const state = thunkAPI.getState()
    const append = {}
    
    if (idx!==undefined && idx !== null) {
      append.siteActiveIdx = idx > state.site.model.sites.length - 1 ? "0" : idx
    }
    
    // David - TODO: find a better way to auto select period index 1 without using $location
    // let paths = ['/app/seo', '/app/report_monthly', '/app/report_annual', '/app/admin_metrics', '/app/summaries_brand', '/app/summaries_company']
    
    let periodIndex
    
    if(state.site.model.period === -1){
      periodIndex = 0
    }
    else{
      periodIndex = state.site.model.period > state.site.model.periods.length - 1 ? 0 : state.site.model.period
    }
    
    append.period = parseInt(periodIndex)
    
    let trialMode = false
    if(!state.session.isAdmin && state.site.model.sites[idx] && state.site.model.singleSiteUser && state.site.model.sites[idx].attr.trialExpiryDate){
      trialMode = true
    }

    let showAdminScreen = true
		if(state.site.model && state.site.model.sites[idx] && 
			state.site.model.sites.find(s => s.siteId === state.session.user.siteId) && 
			(state.site.model.sites[idx].attr.accounts === null 
			|| state.site.model.sites[idx].attr.accounts === undefined
			|| state.site.model.sites[idx].attr.accounts.ga === undefined) ){
			
			showAdminScreen = false
		}

    await thunkAPI.dispatch(setAdminScreen(showAdminScreen))

    await thunkAPI.dispatch(setTrialMode(trialMode))
    
    await thunkAPI.dispatch(appendModel(append))
    
    await thunkAPI.dispatch(periodChange(periodIndex))
    
    
  })
  
  export const periodChange = createAsyncThunk(
  'site/period',
  async (idx, thunkAPI) => {
    // const state = thunkAPI.getState()
    
    const append = {}
    
    if (idx!==undefined) {
      append.period = parseInt(idx)
    }
    
    await thunkAPI.dispatch(appendModel(append))
})

export const getNotifications = createAsyncThunk(
  'site/notifications',
  async ({ siteId, lastEvalKey }, thunkAPI) => {
    const state = await thunkAPI.getState()
    const { response, error } = await getNotificationsBySite(null, siteId, lastEvalKey ?? null)

    if(response){
      await thunkAPI.dispatch(appendModel({        
        notification: {
          items: [...(lastEvalKey ? state.site.model.notification.items : []), ...response.notifications],
          lastEvalKey: response.lastEvalKey
        }
      }))

      return {
        notifications: response.notifications,
        lastEvalKey: response.lastEvalKey
      }
    } else {
      console.log(error)
      await thunkAPI.dispatch(appendModel({        
        notification: {
          items: [],
          lastEvalKey: null
        }
      }))
      return {
        error
      }
    }

})

export const unReadNotifications = (state) => state.site.model.notification?.items?.reduce((prev, curr, idx) => {
  if(curr.users === undefined || 
    curr.users[state.session.user.userId] === undefined ||
    !curr.users[state.session.user.userId].read) {
      prev ++
    }
  return prev
}, 0)

export const siteSlice = createSlice({
  name: 'site',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    populateModel: (state, action) => {
      state.model = action.payload
    },
    appendModel: (state, action) => {
      state.model = {
        ...state.model,
        ...action.payload
      }
    },
    // Using this reducer function is much faster than "appendModel" when dispatching a redux action based on an input change
    modifyModelAttr: (state, action) => {
      state.model[action.payload.key] = action.payload.body
    },
    setStatus: (state, action) => {
      state.status = action.payload
    },
    deleteSite: (state, action) => {
      const { siteId, siteType } = action.payload
      let siteArrName = "topOrganizations"
      switch (siteType) {
        case "ORG":
          siteArrName = "organizations"
          break
        case "REG":
          siteArrName = "regions"
          break
        case "SITE":
          siteArrName = "sites"
          break
        case "TOKEN":
          siteArrName = "salesTokens"
          break
        default:
      }

      const idx = state.model[siteArrName].findIndex(s => s.siteId === siteId)
      const allIdx = state.model?.all.findIndex(s => s.siteId === siteId)

      const sites = state.model[siteArrName]
      const all = state.model.all
      let orgFilter = state.model.organizationsFilter
      let siteFilter = state.model.sitesFilter

      sites.splice(idx, 1)
      all.splice(allIdx, 1)

      if(siteArrName === "organizations"){
        const orgFilterIdx = state.model.organizationsFilter.findIndex(s => s.siteId === siteId)
        orgFilter.splice(orgFilterIdx, 1)
      } else if(siteArrName === "sites"){
        const siteFilterIdx = state.model.sitesFilter.findIndex(s => s.siteId === siteId)
        siteFilter.splice(siteFilterIdx, 1)
      }

      state.model = {
        ...state.model,
        [`${siteType.toLowerCase()}ActiveIdx`]: -1,
        [siteArrName]: [...sites],
        all: [...all],
        organizationsFilter: [...orgFilter],
        sitesFilter: [...siteFilter],
      }

    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSitesAsync.pending, (state) => {
        state.status = 'initialLoading';
      })
      .addCase(getSitesAsync.fulfilled, (state, action) => {
        state.model = {...state.model, ...action.payload}
        state.status = 'idle'
      })
      .addCase(topOrgChange.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(orgChange.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(regChange.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(siteChange.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(periodChange.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(periodChange.fulfilled, (state, action) => {
        state.status = 'fulfilled'
      })
      .addCase(getNotifications.pending, (state, action) => {
        state.model.notification.status = 'loading'
      })
      .addCase(getNotifications.fulfilled, (state, action) => {
        state.model.notification.status = 'fulfilled'
      });
    },
  });
  
  export const { appendModel, modifyModelAttr, setStatus, deleteSite } = siteSlice.actions;
  
  export default siteSlice.reducer;
